import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ListFilter, SlidersHorizontal, ArrowUpDown, Check } from 'lucide-react';
import { useSortBy, useCurrentRefinements } from 'react-instantsearch';
import { useTranslation } from 'react-i18next';

interface MobileSortBarProps {
    onOpenFilter: () => void;
}

const MobileSortBar: React.FC<MobileSortBarProps> = ({ onOpenFilter }) => {
    const { t } = useTranslation('marketplace');
    const [isSortOpen, setIsSortOpen] = useState(false);

    // Sort options - these assume you have these indices configured in Algolia
    const { options: sortItems, refine: sortBy, currentRefinement } = useSortBy({
        items: [
            { label: t('sort.featured'), value: 'listings' },
            { label: t('sort.priceLowToHigh'), value: 'listings_price_asc' },
            { label: t('sort.priceHighToLow'), value: 'listings_price_desc' },
            { label: t('sort.newest'), value: 'listings_created_at_desc' },
        ],
    });

    const activeSort = sortItems.find(item => item.value === currentRefinement) || sortItems[0];
    const { items: activeRefinements } = useCurrentRefinements();
    const filterCount = activeRefinements.reduce((acc, curr) => acc + curr.refinements.length, 0);

    return (
        <>
            <div className="sticky top-[110px] z-20 bg-white/60 dark:bg-gray-950/60 backdrop-blur-md border-b border-gray-100/30 dark:border-gray-800/30 py-2.5 px-4 md:hidden transition-all duration-300">
                <div className="flex items-center justify-between gap-2">

                    {/* Sort Trigger */}
                    <button
                        onClick={() => setIsSortOpen(true)}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-white/50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800/50 rounded-2xl text-[10px] font-black uppercase tracking-[0.15em] text-gray-900 dark:text-white transition-all active:scale-95 shadow-sm"
                    >
                        <ArrowUpDown size={12} className="text-blue-500" />
                        <span className="truncate">{activeSort?.label}</span>
                    </button>

                    {/* Filter Trigger */}
                    <button
                        onClick={onOpenFilter}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-white/50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800/50 rounded-2xl text-[10px] font-black uppercase tracking-[0.15em] text-gray-900 dark:text-white transition-all active:scale-95 relative shadow-sm"
                    >
                        <ListFilter size={12} className="text-purple-500" />
                        <span>{t('mobile.filter')}</span>
                        {filterCount > 0 && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-[10px] font-black shadow-lg animate-in zoom-in duration-300">
                                {filterCount}
                            </span>
                        )}
                    </button>

                </div>
            </div>

            {/* Sort Bottom Sheet */}
            <AnimatePresence>
                {isSortOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsSortOpen(false)}
                            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[110]"
                        />
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-950 rounded-t-[2.5rem] z-[111] border-t border-gray-100 dark:border-gray-800 pb-12"
                        >
                            <div className="flex justify-center pt-3 pb-6">
                                <div className="w-12 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full" />
                            </div>

                            <div className="px-6 mb-4">
                                <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tighter">
                                    {t('mobile.sorting')}
                                </h3>
                            </div>

                            <div className="px-4 space-y-1">
                                {sortItems.map((item) => {
                                    const isSelected = item.value === currentRefinement;
                                    return (
                                        <button
                                            key={item.value}
                                            onClick={() => {
                                                sortBy(item.value);
                                                setIsSortOpen(false);
                                            }}
                                            className={`w-full flex items-center justify-between p-4 rounded-2xl transition-all ${isSelected
                                                ? 'bg-gray-950 dark:bg-white text-white dark:text-gray-950 shadow-xl'
                                                : 'bg-transparent text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-900'
                                                }`}
                                        >
                                            <span className="text-sm font-bold">{item.label}</span>
                                            {isSelected && <Check size={18} strokeWidth={3} />}
                                        </button>
                                    );
                                })}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default MobileSortBar;
